import * as Phaser from "phaser"
import { InputManager } from "../core/input-mapper"
import { AUDIO, SCENE } from "../core/keys"
import { applyMusicVolume } from "../core/music-volume"
import { PersistedData } from "../core/persisted-data"
import { THEME } from "../core/theme"
import { playClick, playHover } from "../core/ui-sfx"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { RetroButton } from "../ui/components"
import { applyVignetteFilter, drawScanlines } from "../ui/neon-fx"

const FONT = THEME.fonts

const SLIDER_W = 220
const SLIDER_H = 12
const STEPS = 10

interface SliderState {
  value: number
  fill: Phaser.GameObjects.Graphics
  label: Phaser.GameObjects.Text
  trackX: number
  trackY: number
}

interface Star {
  x: number
  y: number
  speed: number
  color: number
  alpha: number
  size: number
}

export class OptionsScene extends Phaser.Scene {
  private returnScene: string = SCENE.MENU
  private inputManager!: InputManager
  private selectedIndex = 0
  private sliders: SliderState[] = []
  private menuButtons: RetroButton[] = []
  private starLayers: {
    graphics: Phaser.GameObjects.Graphics
    stars: Star[]
  }[] = []

  constructor() {
    super({ key: SCENE.OPTIONS })
  }

  init(data: { returnTo?: string }): void {
    this.returnScene = data.returnTo ?? SCENE.MENU
    this.selectedIndex = 0
    this.sliders = []
    this.menuButtons = []
    this.starLayers = []
  }

  create(): void {
    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    this.cameras.main.fadeIn(600, 0, 0, 0)

    // 1. Initialize Input first
    this.inputManager = new InputManager(this, {
      onDown: (action) => {
        switch (action) {
          case "UP":
            this.moveSelection(-1)
            break
          case "DOWN":
            this.moveSelection(1)
            break
          case "LEFT":
            this.adjustSlider(-0.1)
            break
          case "RIGHT":
            this.adjustSlider(0.1)
            break
          case "CONFIRM":
          case "FLAP":
            this.confirmSelection()
            break
          case "BACK":
            this.goBack()
            break
        }
      },
    })

    // 2. Background
    this.renderSpaceBackground()
    this.createStarfield()

    // 3. Main Panel
    const panelW = 500
    const panelH = 340
    const panelX = cx - panelW / 2
    const panelY = cy - panelH / 2

    const panel = this.add.graphics()
    panel.fillStyle(0x1a2b5a, 0.85)
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 12)
    panel.lineStyle(2, 0x4a90e2, 0.6)
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 12)

    panel.fillStyle(0x2a407a, 1)
    panel.fillRect(panelX + 10, panelY + 10, panelW - 20, 40)
    panel.lineStyle(1, 0xffffff, 0.2)
    panel.strokeRect(panelX + 10, panelY + 10, panelW - 20, 40)

    // 4. Title
    this.add
      .text(cx, panelY + 30, "SYSTEM CONFIGURATION", {
        fontFamily: FONT.sans,
        fontSize: "18px",
        fontStyle: "900 italic",
        color: "#ffffff",
        letterSpacing: 2,
      })
      .setOrigin(0.5)

    // 5. Volume Controls
    const startY = panelY + 90
    const spacingY = 80

    // Music Volume (Index 0)
    this.sliders.push(
      this.createSlider(
        cx,
        startY,
        "MUSIC EMITTER",
        PersistedData.getMusicVolume(),
        0x4a90e2,
        (v) => {
          PersistedData.setMusicVolume(v)
          applyMusicVolume(this)
        },
      ),
    )

    // SFX Volume (Index 1)
    this.sliders.push(
      this.createSlider(
        cx,
        startY + spacingY,
        "SONIC FEEDBACK",
        PersistedData.getSfxVolume(),
        0x4a90e2,
        (v) => {
          PersistedData.setSfxVolume(v)
          playClick(this)
        },
      ),
    )

    // 6. Back Button (Index 2)
    const backBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: cx - 90,
      y: panelY + panelH - 60,
      width: 180,
      height: 38,
      text: "BACK TO TITLE",
      color: 0x223366,
      isFilled: true,
      onClick: () => this.goBack(),
    })
    this.menuButtons.push(backBtn)

    this.updateUIVisuals()

    // 7. Post FX
    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.05, 3).setDepth(100)
    applyVignetteFilter(this.cameras.main, { strength: 0.25, radius: 0.8 })

    this.events.once("shutdown", () => {
      this.inputManager.destroy()
    })
  }

  update(_time: number, delta: number): void {
    for (const layer of this.starLayers) {
      layer.graphics.clear()
      for (const star of layer.stars) {
        star.y += star.speed * (delta / 16.6)
        if (star.y > GAME_HEIGHT) {
          star.y = -10
          star.x = Phaser.Math.Between(0, GAME_WIDTH)
        }
        layer.graphics.fillStyle(star.color, star.alpha)
        layer.graphics.fillRect(star.x, star.y, star.size, star.size)
      }
    }
  }

  private renderSpaceBackground(): void {
    const bg = this.add.graphics()
    bg.fillGradientStyle(0x0a1128, 0x0a1128, 0x02040a, 0x02040a, 1)
    bg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)
  }

  private createStarfield(): void {
    const configs = [
      { count: 40, speed: 0.2, size: 1, alpha: 0.3, color: 0xffffff },
      { count: 25, speed: 0.5, size: 1, alpha: 0.6, color: 0xaaaaff },
      { count: 15, speed: 1.2, size: 2, alpha: 0.8, color: 0xffffff },
    ]

    for (const config of configs) {
      const graphics = this.add.graphics().setDepth(-10)
      const stars: Star[] = []
      for (let i = 0; i < config.count; i++) {
        stars.push({
          x: Phaser.Math.Between(0, GAME_WIDTH),
          y: Phaser.Math.Between(0, GAME_HEIGHT),
          speed: config.speed,
          size: config.size,
          alpha: config.alpha,
          color: config.color,
        })
      }
      this.starLayers.push({ graphics, stars })
    }
  }

  private createSlider(
    cx: number,
    y: number,
    labelText: string,
    initialValue: number,
    color: number,
    onChange: (value: number) => void,
  ): SliderState {
    const trackX = cx - SLIDER_W / 2
    const trackY = y + 30

    // Label
    this.add.text(trackX, y, labelText, {
      fontFamily: FONT.sans,
      fontSize: "12px",
      fontStyle: "900",
      color: "#44ccff",
      letterSpacing: 2,
    })

    // Value label
    const valueLabel = this.add
      .text(trackX + SLIDER_W, y, `${Math.round(initialValue * 100)}%`, {
        fontFamily: FONT.pixel,
        fontSize: "12px",
        color: "#ffffff",
      })
      .setOrigin(1, 0)

    // Track
    const track = this.add.graphics()
    track.fillStyle(0x000000, 0.4)
    track.fillRect(trackX, trackY, SLIDER_W, SLIDER_H)
    track.lineStyle(1, color, 0.3)
    track.strokeRect(trackX, trackY, SLIDER_W, SLIDER_H)

    // Fill
    const fill = this.add.graphics()

    const state: SliderState = {
      value: initialValue,
      fill,
      label: valueLabel,
      trackX,
      trackY,
    }

    this.drawSliderFill(state, color)

    // Interaction
    const zone = this.add
      .zone(cx, trackY + SLIDER_H / 2, SLIDER_W + 40, SLIDER_H + 40)
      .setInteractive({ useHandCursor: true })

    this.inputManager.bindPointer(zone, {
      pointerdown: (p: Phaser.Input.Pointer) => {
        this.selectedIndex = this.sliders.indexOf(state)
        this.updateUIVisuals()
        this.updateSliderFromPointer(p, state, color, onChange)
      },
      pointermove: (p: Phaser.Input.Pointer) => {
        if (p.isDown) {
          this.updateSliderFromPointer(p, state, color, onChange)
        }
      },
    })

    return state
  }

  private updateSliderFromPointer(
    pointer: Phaser.Input.Pointer,
    state: SliderState,
    color: number,
    onChange: (v: number) => void,
  ): void {
    const localX = pointer.x - state.trackX
    const raw = Phaser.Math.Clamp(localX / SLIDER_W, 0, 1)
    state.value = Math.round(raw * STEPS) / STEPS
    this.updateSlider(state, color, onChange)
  }

  private updateSlider(
    state: SliderState,
    color: number,
    onChange: (v: number) => void,
  ): void {
    this.drawSliderFill(state, color)
    state.label.setText(`${Math.round(state.value * 100)}%`)
    onChange(state.value)
  }

  private drawSliderFill(state: SliderState, color: number): void {
    state.fill.clear()
    const fillW = SLIDER_W * state.value
    if (fillW > 0) {
      // SNES-style banded fill
      const h1 = SLIDER_H * 0.4
      const h2 = SLIDER_H - h1

      const c = Phaser.Display.Color.ValueToColor(color)
      state.fill.fillStyle(c.clone().lighten(20).color, 1)
      state.fill.fillRect(state.trackX, state.trackY, fillW, h1)

      state.fill.fillStyle(c.clone().darken(10).color, 1)
      state.fill.fillRect(state.trackX, state.trackY + h1, fillW, h2)

      // Knob indicator
      state.fill.fillStyle(0xffffff, 1)
      state.fill.fillRect(
        state.trackX + fillW - 2,
        state.trackY - 2,
        4,
        SLIDER_H + 4,
      )
    }
  }

  private moveSelection(dir: number): void {
    playHover(this)
    // 3 options total: Music, SFX, Back Button
    this.selectedIndex = (this.selectedIndex + dir + 3) % 3
    this.updateUIVisuals()
  }

  private updateUIVisuals(): void {
    // Sliders (Index 0, 1)
    this.sliders.forEach((s, i) => {
      const isSelected = this.selectedIndex === i
      s.label.setColor(isSelected ? "#ffffff" : "#44ccff")
      s.label.setAlpha(isSelected ? 1 : 0.7)
    })

    // Back Button (Index 2)
    this.menuButtons.forEach((btn, i) => {
      btn.setSelected(this.selectedIndex === i + 2)
    })
  }

  private adjustSlider(dir: number): void {
    if (this.selectedIndex < 2) {
      const state = this.sliders[this.selectedIndex]
      state.value = Phaser.Math.Clamp(
        Math.round((state.value + dir) * STEPS) / STEPS,
        0,
        1,
      )

      const color = 0x4a90e2
      const onChange = (v: number) => {
        if (this.selectedIndex === 0) {
          PersistedData.setMusicVolume(v)
          applyMusicVolume(this)
        } else {
          PersistedData.setSfxVolume(v)
          playClick(this)
        }
      }
      this.updateSlider(state, color, onChange)
    }
  }

  private confirmSelection(): void {
    if (this.selectedIndex === 2) {
      this.menuButtons[0].click()
    }
  }

  private goBack(): void {
    playClick(this)
    if (this.returnScene === SCENE.PAUSE) {
      this.scene.stop(SCENE.OPTIONS)
      this.scene.launch(SCENE.PAUSE)
    } else {
      this.scene.start(this.returnScene)
    }
  }
}
